import { JobTaskDetailSchema } from "@augmodo/lebron-browser-client";
import {
  mvpApiGetNextPendingJobTaskOptions,
  mvpApiSkipJobTaskMutation,
} from "@labeling-tools/api/lebron/generated/@tanstack/react-query.gen";
import { Button, Group, Modal, Radio, Text } from "@mantine/core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useParams } from "react-router-dom";
import { isTaskAlreadyFinalized } from "@/utils/task-already-finalized";

interface Props {
  taskDetailResp: JobTaskDetailSchema | undefined;
  /** Coverage Beta QA: Skip fires immediately with a fixed reason, no modal. */
  qaMode?: boolean;
}

/**
 * Client-chosen reason for a QA-mode skip — `JobTaskSkipSchema.result` is
 * free-form, so this just keeps QA skips separable from labeler skips in
 * metrics.
 */
const QA_SKIP_REASON = "qa_skip";

export const CoverageBetaSkipBtn = (props: Props) => {
  const { taskDetailResp, qaMode } = props;
  const params = useParams<{ jobId: string }>();
  const job_id = params?.jobId ?? "";
  const task_id = taskDetailResp?.id ?? "";
  const [skipModalOpen, setSkipModalOpen] = useState(false);
  const [skipReason, setSkipReason] = useState("");
  const handleClose = () => {
    setSkipModalOpen(false);
    setSkipReason("");
  };

  const queryClient = useQueryClient();
  const goToNextTask = async () => {
    handleClose();
    await queryClient.invalidateQueries({
      queryKey: mvpApiGetNextPendingJobTaskOptions({ path: { job_id } })
        .queryKey,
      exact: true,
    });
  };
  const skipJobTask = useMutation({
    ...mvpApiSkipJobTaskMutation(),
    onSuccess: goToNextTask,
    onError: (error) => {
      // Already completed/skipped (double-click, second tab): move on.
      if (isTaskAlreadyFinalized(error)) return goToNextTask();
      console.error("skip error");
    },
  });

  const submitSkip = (reason: string) => {
    // Guards a fast double-click: `isPending` flips back to false the instant
    // the response lands, before the next task's id has swapped in, so the
    // task_id check matters even with the button's own `loading` state.
    if (!task_id || skipJobTask.isPending) return;
    skipJobTask.mutate({
      path: { job_id, task_id },
      body: {
        result: {
          skip_reasons: [reason],
          skip_notes: null,
        },
      },
    });
  };

  return (
    <>
      <Button
        variant="outline"
        color="orange"
        size="sm"
        loading={qaMode ? skipJobTask.isPending : undefined}
        disabled={qaMode ? !task_id : undefined}
        onClick={() => {
          if (qaMode) submitSkip(QA_SKIP_REASON);
          else setSkipModalOpen(true);
        }}
      >
        Skip
      </Button>

      {skipModalOpen && (
        <Modal
          opened={skipModalOpen}
          onClose={handleClose}
          title={<Text fw={600}>Skip Task</Text>}
          centered
          zIndex={10000}
          withinPortal
          portalProps={{ target: "body" }}
          lockScroll={false}
          styles={{
            overlay: { position: "fixed", inset: 0 },
            inner: { position: "fixed", inset: 0 },
          }}
        >
          <div className="flex flex-col gap-4">
            <Text size="sm" c="dimmed">
              Select a reason for skipping this task:
            </Text>

            <Radio.Group value={skipReason ?? ""} onChange={setSkipReason}>
              <div className="flex flex-col gap-2">
                {[
                  {
                    value: "unsupported_planogram",
                    label: "Unsupported Planogram",
                  },
                  { value: "poor_image_quality", label: "Poor Image Quality" },
                ].map(({ value, label }) => {
                  return <Radio key={value} value={value} label={label} />;
                })}
              </div>
            </Radio.Group>

            <Group justify="flex-end" mt="sm">
              <Button variant="subtle" color="gray" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                color="orange"
                onClick={() => submitSkip(skipReason)}
                disabled={!skipReason}
                loading={skipJobTask.isPending}
              >
                Confirm Skip
              </Button>
            </Group>
          </div>
        </Modal>
      )}
    </>
  );
};
